import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import React, { useCallback, useEffect, useState } from 'react';

export type QMenuScrollArrowDirection = 'up' | 'down';

export type QMenuScrollArrowProps = {
  direction: QMenuScrollArrowDirection;
  /** Element that scrolls (typically the MenuList). */
  scrollParent: HTMLElement | null;
};

const APPEAR_MS = 160;

/**
 * Scroll chrome for a scrollable menu list.
 * Shown when more content exists above (up) or below (down).
 * Click jumps to the top or bottom of the list.
 * Absolutely positioned over the list so content scrolls under a paper→transparent fade.
 *
 * Mouse affordance only (`aria-hidden`): the fade is non-interactive; only the small
 * icon hit target receives clicks. Keyboard users scroll the list / Escape to dismiss.
 */
export function QMenuScrollArrow({ direction, scrollParent }: QMenuScrollArrowProps): React.ReactElement {
  const [visible, setVisible] = useState(false);

  const updateVisibility = useCallback(() => {
    if (!scrollParent) {
      setVisible(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = scrollParent;
    const canScroll = scrollHeight > clientHeight + 1;
    if (!canScroll) {
      setVisible(false);
      return;
    }
    if (direction === 'up') {
      setVisible(scrollTop > 1);
    } else {
      setVisible(scrollTop + clientHeight < scrollHeight - 1);
    }
  }, [direction, scrollParent]);

  useEffect(() => {
    if (!scrollParent) {
      setVisible(false);
      return undefined;
    }
    updateVisibility();
    scrollParent.addEventListener('scroll', updateVisibility, { passive: true });
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateVisibility) : null;
    resizeObserver?.observe(scrollParent);
    // Options / content size changes (not just the list box) must re-evaluate overflow.
    if (scrollParent.firstElementChild) {
      resizeObserver?.observe(scrollParent.firstElementChild);
    }
    const mutationObserver = typeof MutationObserver !== 'undefined' ? new MutationObserver(updateVisibility) : null;
    mutationObserver?.observe(scrollParent, { childList: true, subtree: true });
    return () => {
      scrollParent.removeEventListener('scroll', updateVisibility);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [scrollParent, updateVisibility]);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (!scrollParent) return;
    scrollParent.scrollTo({
      top: direction === 'up' ? 0 : scrollParent.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
    // Keep menu focus / selection on the list.
    event.preventDefault();
    event.stopPropagation();
  };

  const Icon = direction === 'up' ? KeyboardArrowUpRoundedIcon : KeyboardArrowDownRoundedIcon;

  return (
    <Fade in={visible} timeout={APPEAR_MS} unmountOnExit>
      <Box
        aria-hidden
        data-direction={direction}
        data-visible=''
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          ...(direction === 'up' ? { top: 0 } : { bottom: 0 }),
          zIndex: 1,
          height: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'transparent',
          backgroundImage: (theme) =>
            direction === 'up'
              ? `linear-gradient(to bottom, ${theme.palette.background.paper} 40%, transparent)`
              : `linear-gradient(to top, ${theme.palette.background.paper} 40%, transparent)`,
          color: 'icon.light',
          // Fade is decorative; clicks pass through to menuitems underneath.
          pointerEvents: 'none',
          '&:hover': {
            color: 'text.primary',
          },
        }}
      >
        <Box
          component='span'
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          sx={{
            pointerEvents: 'auto',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Compact hit target — not a full-width overlay.
            p: 0.25,
            lineHeight: 0,
          }}
        >
          <Icon fontSize='small' />
        </Box>
      </Box>
    </Fade>
  );
}
